class HVLargeMenuGallery extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.track = null;
    this.thumb = null;
    this._onScroll = null;
    this._onPointerDown = null;
    this._onPointerMove = null;
    this._onTrackClick = null;
    this._onContainerPointerDown = null;
    this._onKeyDown = null;
    this._onKeyUp = null;
    this._onFocusOut = null;
    this._resizeObserver = null;
    this._dragStartX = 0;
    this._dragStartScroll = 0;
    this._keyScrollDirection = 0;
    this._keyScrollFrame = null;
    this._keyScrollLastTime = 0;
    this._keyScrollSpeed = 520;
  }

  init() {
    this.container = this.querySelector('[data-scroll-container]');
    this.track = this.querySelector('[data-scroll-track]');
    this.thumb = this.querySelector('[data-scroll-thumb]');
  }

  isScrollKeyTarget(target) {
    return this.container?.contains(target);
  }

  focusScrollContainer() {
    this.container?.focus({ preventScroll: true });
  }

  scrollToPosition(left) {
    this.container.scrollTo({ left, behavior: 'smooth' });
  }

  startKeyScroll(direction) {
    if (this._keyScrollDirection === direction) return;

    this.stopKeyScroll(false);
    this._keyScrollDirection = direction;
    this._keyScrollLastTime = 0;
    this._keyScrollFrame = requestAnimationFrame((time) => this.tickKeyScroll(time));
  }

  tickKeyScroll(time) {
    if (!this._keyScrollDirection) return;

    if (!this._keyScrollLastTime) {
      this._keyScrollLastTime = time;
    }

    const elapsed = Math.min(32, time - this._keyScrollLastTime) / 1000;
    this._keyScrollLastTime = time;

    const scrollable = this.container.scrollWidth - this.container.clientWidth;
    if (scrollable <= 0) {
      this.stopKeyScroll();
      return;
    }

    const next = this.container.scrollLeft + this._keyScrollDirection * this._keyScrollSpeed * elapsed;
    this.container.scrollLeft = Math.max(0, Math.min(scrollable, next));

    if (
      (this._keyScrollDirection < 0 && this.container.scrollLeft <= 0) ||
      (this._keyScrollDirection > 0 && this.container.scrollLeft >= scrollable)
    ) {
      this.stopKeyScroll();
      return;
    }

    this._keyScrollFrame = requestAnimationFrame((nextTime) => this.tickKeyScroll(nextTime));
  }

  stopKeyScroll(resetDirection = true) {
    if (this._keyScrollFrame) {
      cancelAnimationFrame(this._keyScrollFrame);
      this._keyScrollFrame = null;
    }

    this._keyScrollLastTime = 0;

    if (resetDirection) {
      this._keyScrollDirection = 0;
    }
  }

  connectedCallback() {
    this.init();
    if (!this.container || !this.track || !this.thumb) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.container;
      const scrollable = scrollWidth - clientWidth;
      if (scrollable <= 0) {
        this.track.style.display = 'none';
        return;
      }

      const thumbWidth = Math.max(40, (clientWidth / scrollWidth) * 100);
      this.track.style.display = '';
      this.thumb.style.width = thumbWidth + '%';
      this.thumb.style.transform = `translateX(${(scrollLeft / scrollable) * (100 - thumbWidth) / 100 * this.track.offsetWidth}px)`;
    };

    this._onScroll = update;
    this.container.addEventListener('scroll', this._onScroll, { passive: true });
    this._resizeObserver = new ResizeObserver(update);
    this._resizeObserver.observe(this.container);
    update();

    this._onKeyDown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!this.isScrollKeyTarget(event.target)) return;

      const scrollable = this.container.scrollWidth - this.container.clientWidth;
      if (scrollable <= 0) return;

      event.preventDefault();
      this.startKeyScroll(event.key === 'ArrowLeft' ? -1 : 1);
    };

    this._onKeyUp = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!this.isScrollKeyTarget(event.target)) return;
      this.stopKeyScroll();
    };

    this._onFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.stopKeyScroll();
    };

    this._onContainerPointerDown = (event) => {
      if (event.target.closest('a')) return;
      this.focusScrollContainer();
    };

    this._onPointerDown = (e) => {
      e.preventDefault();
      this._dragStartX = e.clientX;
      this._dragStartScroll = this.container.scrollLeft;
      this.thumb.setPointerCapture(e.pointerId);
      this.focusScrollContainer();
    };

    this._onPointerMove = (e) => {
      e.preventDefault();
      if (!this.thumb.hasPointerCapture(e.pointerId)) return;
      const trackSpace = this.track.offsetWidth - this.thumb.offsetWidth;
      if (trackSpace <= 0) return;
      const ratio = (this.container.scrollWidth - this.container.clientWidth) / trackSpace;
      this.container.scrollLeft = this._dragStartScroll + (e.clientX - this._dragStartX) * ratio;
    };

    this._onTrackClick = (e) => {
      e.preventDefault();
      if (e.target === this.thumb) return;
      const ratio = (e.clientX - this.track.getBoundingClientRect().left) / this.track.offsetWidth;
      const target = ratio * (this.container.scrollWidth - this.container.clientWidth);
      this.scrollToPosition(target);
      this.focusScrollContainer();
    };

    this.thumb.addEventListener('pointerdown', this._onPointerDown);
    this.thumb.addEventListener('pointermove', this._onPointerMove);
    this.track.addEventListener('click', this._onTrackClick);
    this.container.addEventListener('pointerdown', this._onContainerPointerDown);
    this.addEventListener('keydown', this._onKeyDown, true);
    this.addEventListener('keyup', this._onKeyUp, true);
    this.addEventListener('focusout', this._onFocusOut, true);
  }

  disconnectedCallback() {
    this.stopKeyScroll();

    if (this._onScroll) this.container?.removeEventListener('scroll', this._onScroll);
    if (this._onPointerDown) this.thumb?.removeEventListener('pointerdown', this._onPointerDown);
    if (this._onPointerMove) this.thumb?.removeEventListener('pointermove', this._onPointerMove);
    if (this._onTrackClick) this.track?.removeEventListener('click', this._onTrackClick);
    if (this._onContainerPointerDown) this.container?.removeEventListener('pointerdown', this._onContainerPointerDown);
    if (this._onKeyDown) this.removeEventListener('keydown', this._onKeyDown, true);
    if (this._onKeyUp) this.removeEventListener('keyup', this._onKeyUp, true);
    if (this._onFocusOut) this.removeEventListener('focusout', this._onFocusOut, true);
    this._resizeObserver?.disconnect();
  }
}

if (!customElements.get('hv-large-menu-gallery')) {
  customElements.define('hv-large-menu-gallery', HVLargeMenuGallery);
}
